import { useDateFormatter } from "@react-aria/i18n";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Camera, ChevronRight } from "lucide-react";
import z from "zod";

import { isAdminRole, useViewer } from "@/auth/shared";
import { Link } from "@/components/base/link";
import { Pagination } from "@/components/base/pagination";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getImageUrl } from "@/supabase/storage";

const searchSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(50).default(10),
});

export const Route = createFileRoute("/gallery")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
});

function RouteComponent() {
  const trpc = useTRPC();
  const viewer = useViewer();
  const isAdmin = isAdminRole(viewer?.role);
  const { page, pageSize } = Route.useSearch();

  const { data, isLoading } = useQuery(
    trpc.gallery.browseTournamentGalleries.queryOptions({ page, pageSize }),
  );

  const { data: pendingTournaments } = useQuery({
    ...trpc.gallery.getPendingTournaments.queryOptions(),
    enabled: isAdmin,
  });

  const dateFormatter = useDateFormatter({
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  return (
    <DefaultLayout>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
        <div className="flex items-center justify-between">
          <h1 className={title()}>Photo Gallery</h1>
        </div>

        {isAdmin && pendingTournaments && pendingTournaments.length > 0 && (
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <h2 className={title({ size: "sm" })}>Pending Approval</h2>
            <div className="flex flex-col gap-1">
              {pendingTournaments.map((t) => (
                <Link
                  key={t.tournamentId}
                  to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                  params={{
                    tournamentId: t.tournamentId.toString(),
                    divisionId: t.divisionId.toString(),
                    tab: "gallery",
                  }}
                  className="flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-amber-100"
                >
                  <span>
                    <span className="font-medium">
                      {t.tournamentName ??
                        dateFormatter.format(new Date(t.tournamentDate))}
                    </span>
                    {t.tournamentName && (
                      <span className="ml-2 text-gray-500">
                        {dateFormatter.format(new Date(t.tournamentDate))}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1 text-amber-700">
                    {t.pendingCount} pending
                    <ChevronRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="overflow-hidden rounded-lg bg-white shadow-sm"
              >
                <div className="max-h-80 min-h-80 w-full animate-pulse bg-gray-100" />
                <div className="flex flex-col gap-2 px-4 py-3">
                  <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        )}

        {data && data.tournaments.length === 0 && page === 1 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Camera size={40} className="text-gray-300" />
            <p className="text-gray-500">
              Our photo gallery is brand new! Check back soon for tournament
              photos.
            </p>
          </div>
        )}

        {data && data.tournaments.length > 0 && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 md:gap-6">
            {data.tournaments.map((tournament) => (
              <Link
                key={tournament.id}
                to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                params={{
                  tournamentId: tournament.id.toString(),
                  divisionId: tournament.divisionId.toString(),
                  tab: "gallery",
                }}
                variant="alt"
                className="overflow-hidden rounded-lg bg-white shadow-sm"
              >
                {tournament.coverPhoto ? (
                  <img
                    src={getImageUrl(
                      "tournament_gallery_photos",
                      tournament.coverPhoto.storagePath,
                      { width: 600, height: 600, quality: 75 },
                    )}
                    alt={
                      tournament.coverPhoto.caption ??
                      tournament.name ??
                      tournament.coverPhoto.filename
                    }
                    loading="lazy"
                    className="max-h-80 min-h-80 w-full object-cover"
                  />
                ) : (
                  <div className="flex max-h-80 min-h-80 w-full items-center justify-center bg-gray-100">
                    <Camera size={40} className="text-gray-300" />
                  </div>
                )}
                <div className="flex flex-col gap-1 px-4 py-3">
                  <span className="text-lg font-semibold">
                    {tournament.name ??
                      [tournament.venueName, tournament.venueCity]
                        .filter(Boolean)
                        .join(", ")}
                  </span>
                  <span className="text-sm text-gray-500">
                    {dateFormatter.format(new Date(tournament.date))}
                    {tournament.name &&
                      ` · ${[tournament.venueName, tournament.venueCity].filter(Boolean).join(", ")}`}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {data && data.pageInfo.totalPages > 1 && (
          <Pagination
            to="/gallery"
            page={page}
            pageSize={pageSize}
            pageInfo={data.pageInfo}
          />
        )}
      </div>
    </DefaultLayout>
  );
}
